import {
  CLAVES_DE_FILTRO_SEGUIMIENTO,
  type FiltrosDeSeguimiento,
  type FiltrosDeSeguimientoConsulta,
} from '@cnc/contracts';
import {useEffect, useState, type ReactNode} from 'react';
import {tr} from '../i18n/idioma';
import {mes} from '../utilidades/formato';
import {AYUDA_FILTROS_SEGUIMIENTO} from './ayuda-seguimiento';
import {NotaDeAyuda} from './nota-de-ayuda';
import {opcionesDe} from './opciones-de-filtro';
import {SelectorDeFiltro} from './selector-de-filtro';

// Los que van bajo "Más filtros".
const FILTROS_SECUNDARIOS: Array<keyof FiltrosDeSeguimientoConsulta> = [
  'tipo',
  'naviera',
  'facturadora',
  'etdDesde',
  'etdHasta',
  'etaDesde',
  'etaHasta',
];

type Props = {
  consulta: FiltrosDeSeguimientoConsulta;
  filtros: FiltrosDeSeguimiento | undefined;
  onCambiar: (cambios: Partial<FiltrosDeSeguimientoConsulta>) => void;
  onLimpiar: () => void;
  // Filtros de las columnas agregadas a la tabla (7/10/2026), debajo de los fijos.
  porColumna?: ReactNode;
};

// Filtros del seguimiento. Las listas vienen encadenadas del servidor: cada
// una muestra solo lo que existe con los demas filtros puestos.
export function FiltrosSeguimiento({consulta, filtros, onCambiar, onLimpiar, porColumna}: Props) {
  const [busqueda, setBusqueda] = useState(consulta.buscar ?? '');

  // La busqueda espera a que se deje de escribir antes de consultar.
  useEffect(() => {
    const temporizador = setTimeout(() => {
      if ((consulta.buscar ?? '') !== busqueda.trim()) onCambiar({buscar: busqueda.trim() || undefined});
    }, 350);
    return () => clearTimeout(temporizador);
  }, [busqueda, consulta.buscar, onCambiar]);
  useEffect(() => setBusqueda(consulta.buscar ?? ''), [consulta.buscar]);

  const activos = CLAVES_DE_FILTRO_SEGUIMIENTO.filter(
    clave => consulta[clave] !== undefined && consulta[clave] !== '',
  ).length;
  const etiquetaDeMes = (sinTexto: string) => (v: string) => (v === 'sin' ? sinTexto : mes(v));

  // Arriba solo los principales; "Más filtros" despliega los demas (abierto si alguno esta puesto).
  const ocultosActivos = FILTROS_SECUNDARIOS.filter(
    clave => consulta[clave] !== undefined && consulta[clave] !== '',
  ).length;
  const [mas, setMas] = useState(ocultosActivos > 0);
  const campoFecha = 'mt-0.5 w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta';

  return (
    <div className="rounded-lg border border-linea bg-white px-3 py-2">
      <div className="grid items-end gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={AYUDA_FILTROS_SEGUIMIENTO.buscar} dentroDeEtiqueta />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={tr(
              'Exportación, booking, buque, factura, agente, naviera o puerto',
              'Export, booking, vessel, invoice, agent, shipping line or port',
            )}
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        <SelectorDeFiltro
          etiqueta={tr('Estado', 'Status')}
          valor={consulta.estado}
          opciones={opcionesDe(filtros?.estados)}
          onCambiar={v => onCambiar({estado: v})}
          ayuda={AYUDA_FILTROS_SEGUIMIENTO.estado}
        />
        <SelectorDeFiltro
          etiqueta={tr('Exportador', 'Exporter')}
          valor={consulta.exportador}
          opciones={opcionesDe(filtros?.exportadores)}
          onCambiar={v => onCambiar({exportador: v})}
          ayuda={AYUDA_FILTROS_SEGUIMIENTO.exportador}
        />
        <SelectorDeFiltro
          etiqueta={tr('Mes de zarpe (ETD)', 'Departure month (ETD)')}
          valor={consulta.mesEtd}
          opciones={opcionesDe(filtros?.mesesEtd, etiquetaDeMes(tr('Sin ETD', 'No ETD')))}
          onCambiar={v => onCambiar({mesEtd: v})}
          ayuda={AYUDA_FILTROS_SEGUIMIENTO.mesEtd}
        />
        <SelectorDeFiltro
          etiqueta={tr('Mes de llegada (ETA)', 'Arrival month (ETA)')}
          valor={consulta.mesEta}
          opciones={opcionesDe(filtros?.mesesEta, etiquetaDeMes(tr('Sin ETA', 'No ETA')))}
          onCambiar={v => onCambiar({mesEta: v})}
          ayuda={AYUDA_FILTROS_SEGUIMIENTO.mesEta}
        />
        <button
          type="button"
          aria-expanded={mas}
          onClick={() => setMas(v => !v)}
          className="rounded-md border border-linea bg-white px-2 py-1 text-xs text-tinta hover:bg-papel"
        >
          {mas ? tr('▴ Menos filtros', '▴ Fewer filters') : tr('▾ Más filtros', '▾ More filters')}
          {ocultosActivos > 0 && <span className="ml-1 font-semibold text-verde-oscuro">({ocultosActivos})</span>}
        </button>
      </div>
      {mas && (
        <div className="mt-2 grid items-end gap-2 border-t border-linea/70 pt-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <SelectorDeFiltro
            etiqueta={tr('Tipo', 'Type')}
            valor={consulta.tipo}
            opciones={opcionesDe(filtros?.tipos)}
            onCambiar={v => onCambiar({tipo: v})}
            ayuda={AYUDA_FILTROS_SEGUIMIENTO.tipo}
          />
          <SelectorDeFiltro
            etiqueta={tr('Naviera', 'Shipping line')}
            valor={consulta.naviera}
            opciones={opcionesDe(filtros?.navieras)}
            onCambiar={v => onCambiar({naviera: v})}
            ayuda={AYUDA_FILTROS_SEGUIMIENTO.naviera}
          />
          <SelectorDeFiltro
            etiqueta={tr('Empresa que factura', 'Invoicing company')}
            valor={consulta.facturadora}
            opciones={opcionesDe(filtros?.facturadoras)}
            onCambiar={v => onCambiar({facturadora: v})}
            ayuda={AYUDA_FILTROS_SEGUIMIENTO.facturadora}
          />
          <label className="block text-xs text-apagado">
            {tr('Zarpe (ETD) desde', 'Departure (ETD) from')}
            <NotaDeAyuda texto={AYUDA_FILTROS_SEGUIMIENTO.etdDesde} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etdDesde ?? ''}
              onChange={e => onCambiar({etdDesde: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Zarpe (ETD) hasta', 'Departure (ETD) to')}
            <NotaDeAyuda texto={AYUDA_FILTROS_SEGUIMIENTO.etdHasta} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etdHasta ?? ''}
              onChange={e => onCambiar({etdHasta: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Llegada (ETA) desde', 'Arrival (ETA) from')}
            <NotaDeAyuda texto={AYUDA_FILTROS_SEGUIMIENTO.etaDesde} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etaDesde ?? ''}
              onChange={e => onCambiar({etaDesde: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Llegada (ETA) hasta', 'Arrival (ETA) to')}
            <NotaDeAyuda texto={AYUDA_FILTROS_SEGUIMIENTO.etaHasta} dentroDeEtiqueta />
            <input
              type="date"
              value={consulta.etaHasta ?? ''}
              onChange={e => onCambiar({etaHasta: e.target.value || undefined})}
              className={campoFecha}
            />
          </label>
        </div>
      )}
      {porColumna}
      {activos > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs text-apagado">
          <span>
            {tr(
              `${activos} filtro${activos === 1 ? '' : 's'} activo${activos === 1 ? '' : 's'}`,
              `${activos} active filter${activos === 1 ? '' : 's'}`,
            )}
          </span>
          <button type="button" onClick={onLimpiar} className="text-verde hover:underline">
            {tr('Quitar filtros', 'Clear filters')}
          </button>
        </div>
      )}
    </div>
  );
}
